import { Button, CircularProgress, Dialog, DialogContent } from "@mui/material";
import { loadStripe, PaymentMethod, Stripe } from "@stripe/stripe-js";
import React, { useCallback, useEffect, useState } from "react";

import { STRIPE_KEY } from "constants/env";
import { SLAPSHOT_COMFYUI_GUIDE_URL } from "constants/urls";
import { Subscription } from "models/User";
import {
  getAutopilotBillingStatus,
  getAutopilotUser,
  getAutopilotUserUsage,
  revokeAutopilotApiKey,
  updateAutopilotSubscription,
  AutopilotUser,
  AutopilotUserUsage,
} from "services/AutopilotService";
import { getPaymentMethods } from "services/SubscriptionService";
import { getUser } from "utils/helper";

import ApiKeysTab from "./ApiKeysTab";
import ApiTabBar, { DeveloperApiTab } from "./ApiTabBar";
import AutopilotBillingTab from "./AutopilotBillingTab";
import BillingStatusBanner from "./BillingStatusBanner";
import CreateApiKeyModal from "./CreateApiKeyModal";
import FailedPaymentPanel from "./FailedPaymentPanel";
import RegenerateApiKeyModal from "./RegenerateApiKeyModal";
import UpgradePaymentPanel from "./UpgradePaymentPanel";
import UsageTab from "./UsageTab";
import styles from "../ProfilePage.module.scss";

interface PaymentPrep {
  stripe: Stripe;
  paymentMethods: PaymentMethod[];
  defaultMethod: PaymentMethod | null;
}

interface DeveloperSectionProps {
  subscription: Subscription | null;
}

const DeveloperSection: React.FC<DeveloperSectionProps> = ({
  subscription,
}) => {
  const user = getUser();

  const [activeTab, setActiveTab] = useState<DeveloperApiTab>("keys");

  const [autopilotUser, setAutopilotUser] = useState<
    AutopilotUser | null | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [generatedApiKey, setGeneratedApiKey] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [deleteKeyLoadingPrefix, setDeleteKeyLoadingPrefix] = useState<
    string | null
  >(null);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [subscriptionUpdateLoading, setSubscriptionUpdateLoading] =
    useState(false);
  const [upgradingInline, setUpgradingInline] = useState(false);

  const [failedInvoice, setFailedInvoice] = useState<{
    id: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [payPrep, setPayPrep] = useState<PaymentPrep | null>(null);
  const [payPreparing, setPayPreparing] = useState(false);
  const [payingInline, setPayingInline] = useState(false);
  const [showFailedAlert, setShowFailedAlert] = useState(false);
  const [paymentResolved, setPaymentResolved] = useState(false);

  const [usage, setUsage] = useState<AutopilotUserUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const loadAutopilotUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAutopilotUser(user.email);
      setAutopilotUser(result);
    } catch {
      setError("Failed to load developer data.");
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    if (autopilotUser === undefined) loadAutopilotUser();
  }, [autopilotUser, loadAutopilotUser]);

  useEffect(() => {
    if (autopilotUser?.company?.renewal_date) setPaymentResolved(false);
  }, [autopilotUser]);

  const refreshUntilRenewed = useCallback(async () => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const result = await getAutopilotUser(user.email);
      setAutopilotUser(result);
      if (result?.company?.renewal_date) return;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });
    }
  }, [user.email]);

  useEffect(() => {
    if (autopilotUser && !usage && !usageLoading) {
      setUsageLoading(true);
      getAutopilotUserUsage(user.email)
        .then(setUsage)
        .catch(() => setError("Failed to load developer usage data."))
        .finally(() => setUsageLoading(false));
    }
  }, [autopilotUser, usage, usageLoading, user.email]);

  useEffect(() => {
    getAutopilotBillingStatus()
      .then((status) => {
        if (status.has_failed_payment && status.invoice) {
          setFailedInvoice(status.invoice);
          setShowFailedAlert(true);
        }
      })
      .catch(() => {});
  }, []);

  const preparePayment = useCallback(async () => {
    if (!subscription) return;
    setPayPreparing(true);
    try {
      const stripe = await loadStripe(STRIPE_KEY);
      const data = await getPaymentMethods(subscription.id);
      if (stripe) {
        setPayPrep({
          stripe,
          paymentMethods: data.payment_methods,
          defaultMethod: data.default_payment_method || null,
        });
      }
    } catch {
      // empty
    } finally {
      setPayPreparing(false);
    }
  }, [subscription]);

  useEffect(() => {
    if (failedInvoice && subscription && !payPrep && !payPreparing) {
      preparePayment();
    }
  }, [failedInvoice, subscription, payPrep, payPreparing, preparePayment]);

  const handleUpgrade = useCallback(async () => {
    setSubscriptionUpdateLoading(true);
    try {
      const res = await updateAutopilotSubscription(user.email, "bill_tier");
      if (res.requires_payment_method) {
        if (!subscription) {
          setError("Unable to start upgrade. Please try again later.");
          return;
        }
        setUpgradingInline(true);
        return;
      }
      await loadAutopilotUser();
    } catch {
      setError("Failed to update subscription.");
    } finally {
      setSubscriptionUpdateLoading(false);
    }
  }, [user.email, loadAutopilotUser, subscription]);

  const handlePaymentMethodReady = useCallback(async () => {
    setUpgradingInline(false);
    setSubscriptionUpdateLoading(true);
    try {
      await updateAutopilotSubscription(user.email, "bill_tier");
      await loadAutopilotUser();
    } catch {
      setError("Failed to update subscription.");
    } finally {
      setSubscriptionUpdateLoading(false);
    }
  }, [user.email, loadAutopilotUser]);

  const handleDeleteKey = useCallback(
    async (prefix: string) => {
      setDeleteKeyLoadingPrefix(prefix);
      try {
        await revokeAutopilotApiKey(user.email, prefix);
        await loadAutopilotUser();
      } catch {
        setError("Failed to delete key.");
      } finally {
        setDeleteKeyLoadingPrefix(null);
      }
    },
    [user.email, loadAutopilotUser],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!keyToDelete) return;
    const prefix = keyToDelete;
    setKeyToDelete(null);
    await handleDeleteKey(prefix);
  }, [keyToDelete, handleDeleteKey]);

  const handleKeyCreated = useCallback(
    (apiKey: string) => {
      setGeneratedApiKey(apiKey);
      setShowCreateModal(false);
      loadAutopilotUser();
    },
    [loadAutopilotUser],
  );

  const handleKeyRegenerated = useCallback(
    (apiKey: string) => {
      setGeneratedApiKey(apiKey);
      setShowRegenerateModal(false);
      loadAutopilotUser();
    },
    [loadAutopilotUser],
  );

  const showUpgradePanel = upgradingInline && !!subscription;
  const showFailedPanel =
    !showUpgradePanel &&
    payingInline &&
    !!payPrep &&
    !!failedInvoice &&
    !!subscription;
  const showMainContent = !showUpgradePanel && !showFailedPanel;

  return (
    <div className={styles.tabPanal}>
      <div className={styles.contentHeader}>
        <h3>ComfyUI</h3>
        {autopilotUser?.company?.subscription_tier === "bill_tier" && (
          <span className={styles.developerSubscriptionTierBadge}>
            Paid Plan
          </span>
        )}
      </div>

      {!payingInline && !upgradingInline && (
        <ApiTabBar activeTab={activeTab} onChange={setActiveTab} />
      )}

      <div className={styles.developerContainer}>
        {showUpgradePanel && subscription && (
          <UpgradePaymentPanel
            subscription={subscription}
            onBack={() => setUpgradingInline(false)}
            onPaymentMethodReady={handlePaymentMethodReady}
          />
        )}

        {showFailedPanel && payPrep && failedInvoice && subscription && (
          <FailedPaymentPanel
            subscription={subscription}
            invoice={failedInvoice}
            stripe={payPrep.stripe}
            paymentMethods={payPrep.paymentMethods}
            defaultMethod={payPrep.defaultMethod}
            onBack={() => setPayingInline(false)}
            onPaid={() => {
              setPayingInline(false);
              setFailedInvoice(null);
              setPayPrep(null);
              setPaymentResolved(true);
              refreshUntilRenewed();
            }}
            onMethodsChanged={preparePayment}
          />
        )}

        {showMainContent && (
          <>
            {activeTab === "keys" && (
              <div className={styles.comfyUiInfoBox}>
                <h5>
                  Slapshot now has ComfyUI nodes. Getting it setup is real easy
                  — just follow these steps:
                </h5>
                <ol>
                  <li>Install ComfyUI nodes</li>
                  <li>
                    Get an API key by clicking the Generate API Key button
                  </li>
                  <li>Insert the API key in your ComfyUI settings</li>
                </ol>
                <p>
                  Read our{" "}
                  <a
                    className={styles.comfyUiGuideLink}
                    href={SLAPSHOT_COMFYUI_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ComfyUI setup guide
                  </a>{" "}
                  if you need more assistance.
                </p>
              </div>
            )}

            {failedInvoice && (
              <BillingStatusBanner
                state="failed"
                loading={!payPrep}
                onPay={() => setPayingInline(true)}
                onResume={handleUpgrade}
              />
            )}

            {!failedInvoice &&
              !paymentResolved &&
              autopilotUser?.company?.subscription_tier === "bill_tier" &&
              !autopilotUser.company.renewal_date && (
                <BillingStatusBanner
                  state="cancelled"
                  loading={subscriptionUpdateLoading}
                  onPay={() => setPayingInline(true)}
                  onResume={handleUpgrade}
                />
              )}

            {loading && (
              <div className={styles.developerCenter}>
                <CircularProgress size={28} sx={{ color: "#bdb8ff" }} />
              </div>
            )}

            {!loading && error && (
              <p className={styles.developerError}>{error}</p>
            )}

            {activeTab === "keys" && (
              <ApiKeysTab
                autopilotUser={autopilotUser}
                loading={loading}
                error={error}
                generatedApiKey={generatedApiKey}
                onClearGeneratedKey={() => setGeneratedApiKey(null)}
                subscriptionUpdateLoading={subscriptionUpdateLoading}
                subscriptionReady={!!subscription}
                onUpgrade={handleUpgrade}
                onGenerateFirstKey={() => setShowCreateModal(true)}
                onAddNewKey={() => setShowRegenerateModal(true)}
                deleteLoadingPrefix={deleteKeyLoadingPrefix}
                onDeleteKey={(prefix) => setKeyToDelete(prefix)}
              />
            )}

            {activeTab === "usage" && (
              <UsageTab usage={usage} loading={usageLoading} />
            )}

            {activeTab === "billing" &&
              (subscription ? (
                <AutopilotBillingTab
                  subscription={subscription}
                  renewalDate={autopilotUser?.company?.renewal_date ?? null}
                  estimatedCost={autopilotUser?.company?.estimated_cost ?? null}
                />
              ) : (
                <div className={styles.developerCenter}>
                  <CircularProgress size={28} sx={{ color: "#bdb8ff" }} />
                </div>
              ))}
          </>
        )}
      </div>

      <CreateApiKeyModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        defaultEmail={user.email}
        defaultFirstName={user.name || ""}
        onCreated={handleKeyCreated}
      />

      <RegenerateApiKeyModal
        open={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        userEmail={user.email}
        onGenerated={handleKeyRegenerated}
        onError={setError}
      />

      <Dialog
        open={!!keyToDelete}
        onClose={() => setKeyToDelete(null)}
        slotProps={{
          paper: {
            className: styles.apiKeyModal,
            sx: { minWidth: 420 },
          },
        }}
      >
        <DialogContent className={styles.apiKeyModalContent}>
          <h2 className={styles.apiKeyModalTitle}>Revoke API key?</h2>
          <p className={styles.apiKeyModalSubtitle}>
            This will permanently revoke this API key. Any integrations using it
            will stop working immediately. This action cannot be undone.
          </p>
          <div className={styles.formActions}>
            <Button
              className="noBackgroundButton"
              onClick={() => setKeyToDelete(null)}
            >
              Cancel
            </Button>
            <Button className="slapShotButton" onClick={handleConfirmDelete}>
              Revoke key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showFailedAlert && !!failedInvoice}
        onClose={() => setShowFailedAlert(false)}
        slotProps={{
          paper: {
            className: styles.apiKeyModal,
            sx: { minWidth: 420 },
          },
        }}
      >
        <DialogContent className={styles.apiKeyModalContent}>
          <h2 className={styles.apiKeyModalTitle}>Your last payment failed</h2>
          <p className={styles.apiKeyModalSubtitle}>
            Your recent API usage payment could not be processed, so the API is
            paused. Complete the payment to resume access.
          </p>
          <div className={styles.formActions}>
            <Button
              className="noBackgroundButton"
              onClick={() => setShowFailedAlert(false)}
            >
              Later
            </Button>
            <Button
              className="slapShotButton"
              disabled={!payPrep}
              onClick={() => {
                setShowFailedAlert(false);
                setPayingInline(true);
              }}
            >
              {payPrep ? (
                "Pay now"
              ) : (
                <CircularProgress size={18} sx={{ color: "white" }} />
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeveloperSection;
